/**
 * Account Audit Trade Matcher Utility
 * Matches and pairs trades between Account A and Account B, calculating execution slippage,
 * timing lag, and discrepancy flags.
 */

// Helper to clean symbol
export const cleanSymbol = (sym) => {
    if (!sym) return '';
    return String(sym)
        .replace(/^(MCX|NSE|NFO|BFO|CDS):/i, '')
        .trim()
        .toUpperCase();
};

// Helper to clean strategy
export const cleanStrategy = (strat) => {
    if (!strat || strat === 'N/A' || strat === '-') return '';
    return String(strat).trim().toUpperCase();
};

// Helper to clean time string
export const cleanTime = (val) => {
    if (!val || val === '-' || val === 'null' || val === 'undefined' || val === 'None' || val === 'N/A') return null;
    const s = String(val).trim();
    if (!s || s === '-' || s === 'null' || s === 'undefined' || s === 'None' || s === 'N/A') return null;
    return s;
};

// Helper to parse dates to milliseconds
export const parseTimeToMs = (val) => {
    if (!val) return null;
    if (typeof val === 'number') {
        return val > 1e11 ? val : val * 1000;
    }
    const cleanStr = String(val).trim();
    if (!cleanStr || cleanStr === '-' || cleanStr === 'null' || cleanStr === 'undefined') return null;

    // If HH:MM:SS or HH:MM format without date, convert to ms from start of day
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(cleanStr)) {
        const parts = cleanStr.split(':').map(Number);
        const hours = parts[0] || 0;
        const minutes = parts[1] || 0;
        const seconds = parts[2] || 0;
        return (hours * 3600 + minutes * 60 + seconds) * 1000;
    }

    const d = new Date(cleanStr.includes('T') ? cleanStr : cleanStr.replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d.getTime();
};

/**
 * Normalizes raw trade records from various Godseye endpoints
 * (such as live_trades_data, client-audit, audit-results, or buy-signal-audit)
 */
export const normalizeAccountTrade = (raw, accountLabel = 'A') => {
    if (!raw) return null;

    // Symbol MUST come from actual live execution (symbol_l or live symbol)
    // If only symbol_b is present without symbol_l, this client never took the trade (it was a backtest signal only)
    const symbol = cleanSymbol(raw.symbol_l || raw.symbol || raw.Symbol || raw.tradingsymbol || raw.symbol_a || '');
    if (!symbol) return null; // Discard unexecuted backtest signal templates

    // Strategy
    const strategy = cleanStrategy(raw.strategy_tag_l || raw.strategy || raw.Strategy || raw.strategy_tag || raw.tag || raw.strategy_tag_a || '');

    // Prices (only accept valid positive live executed prices)
    const rawBuyPrice = raw.buy_price_l ?? raw.buy_price_a ?? raw.buy_price ?? raw.entry_price ?? raw.buyPrice ?? (raw.side === 1 || raw.side === 'BUY' ? raw.price : null);
    const rawSellPrice = raw.sell_price_l ?? raw.sell_price_a ?? raw.sell_price ?? raw.exit_price ?? raw.sellPrice ?? (raw.side === -1 || raw.side === 2 || raw.side === 'SELL' ? raw.price : null);
    
    const buyPrice = rawBuyPrice !== null && rawBuyPrice !== undefined && Number(rawBuyPrice) > 0 ? Number(rawBuyPrice) : null;
    const sellPrice = rawSellPrice !== null && rawSellPrice !== undefined && Number(rawSellPrice) > 0 ? Number(rawSellPrice) : null;

    // Execution Timestamps (robust resolution across all Godseye endpoints and database columns)
    const buyTime = cleanTime(raw.buy_time_l) || 
                    cleanTime(raw.buy_time) || 
                    cleanTime(raw.buy_time_a) || 
                    cleanTime(raw.buy_time_b) || 
                    cleanTime(raw.entry_time) || 
                    cleanTime(raw.entry_timestamp) || 
                    cleanTime(raw.entry_datetime) || 
                    cleanTime(raw.buy_datetime) || 
                    cleanTime(raw.buy_timestamp) || 
                    cleanTime(raw.buyTime) ||
                    cleanTime(raw.order_time) || 
                    cleanTime(raw.order_time_l) ||
                    cleanTime(raw.trade_time) || 
                    cleanTime(raw.trade_time_l) ||
                    cleanTime(raw.traded_time) || 
                    cleanTime(raw.order_datetime) || 
                    cleanTime(raw.order_date_time) ||
                    cleanTime(raw.orderTime) ||
                    cleanTime(raw.side === 1 || raw.side === 'BUY' ? raw.trade_time || raw.order_time || raw.timestamp : null) || 
                    cleanTime(raw.created_at) || 
                    cleanTime(raw.timestamp) || 
                    cleanTime(raw.trade_date) || 
                    cleanTime(raw.date) || 
                    null;

    const sellTime = cleanTime(raw.sell_time_l) || 
                     cleanTime(raw.sell_time) || 
                     cleanTime(raw.sell_time_a) || 
                     cleanTime(raw.sell_time_b) || 
                     cleanTime(raw.exit_time) || 
                     cleanTime(raw.exit_timestamp) || 
                     cleanTime(raw.exit_datetime) || 
                     cleanTime(raw.sell_datetime) || 
                     cleanTime(raw.sell_timestamp) || 
                     cleanTime(raw.sellTime) ||
                     cleanTime(raw.exitTime) ||
                     cleanTime(raw.sell_trade_time) ||
                     cleanTime(raw.side === -1 || raw.side === 2 || raw.side === 'SELL' ? raw.trade_time || raw.order_time || raw.timestamp : null) || 
                     cleanTime(raw.updated_at) || 
                     null;

    // If client has NO live symbol and NO price and NO execution time, discard this record
    const hasLiveExecution = raw.symbol_l || (buyPrice !== null || sellPrice !== null) || (buyTime !== null || sellTime !== null);
    if (!hasLiveExecution) return null;

    // Quantity
    const qty = raw.qty_l ?? raw.qty_a ?? raw.qty ?? raw.quantity ?? raw.net_qty ?? raw.traded_qty ?? null;
    
    // Client ID
    const clientId = raw.client_id || raw.clientId || raw.client || '';

    return {
        id: raw.id || raw.trade_id || raw.order_id || `${symbol}_${buyTime}_${sellTime}_${Math.random().toString(36).substring(2, 7)}`,
        clientId,
        symbol,
        rawSymbol: raw.symbol_l || raw.symbol || raw.tradingsymbol || symbol,
        strategy: strategy || 'Optimus',
        buyTime: buyTime || null,
        buyPrice,
        sellTime: sellTime || null,
        sellPrice,
        qty: qty !== null && qty !== undefined ? Number(qty) : null,
        pnl: raw.pnl !== undefined ? Number(raw.pnl) : null,
        charges: raw.charges || null,
        rawTrade: raw,
        accountLabel
    };
};

/**
 * Robust DateTime formatter that formats ISO, UTC, epoch timestamps, naive time strings, or dates
 */
export const formatDisplayTime = (val) => {
    if (!val || val === '-' || val === 'null' || val === 'undefined') return '-';
    
    // If it's a number (timestamp)
    if (typeof val === 'number') {
        const ms = val > 1e11 ? val : val * 1000;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) {
            const datePart = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
            return `${datePart} ${timePart}`;
        }
    }

    // If it's a string
    if (typeof val === 'string') {
        const cleanStr = val.trim();
        if (!cleanStr || cleanStr === '-' || cleanStr === 'null' || cleanStr === 'undefined') return '-';

        // If it's already HH:MM:SS or HH:MM or HH:MM:SS.mmm
        if (/^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(cleanStr)) {
            return cleanStr;
        }

        // If it's a numeric string (epoch ms or sec)
        if (/^\d{10,13}$/.test(cleanStr)) {
            const num = Number(cleanStr);
            const ms = num > 1e11 ? num : num * 1000;
            const d = new Date(ms);
            if (!isNaN(d.getTime())) {
                const datePart = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                return `${datePart} ${timePart}`;
            }
        }

        // Try standard Date parsing (handle "YYYY-MM-DD HH:MM:SS" or ISO)
        const formattedStr = cleanStr.includes('T') ? cleanStr : cleanStr.replace(' ', 'T');
        const d = new Date(formattedStr);
        if (!isNaN(d.getTime())) {
            const datePart = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
            return `${datePart} ${timePart}`;
        }

        return cleanStr;
    }

    return String(val);
};

/**
 * Normalizes an array of raw items (trades, positions, or raw orders) for a client
 */
export const processAccountRecords = (records = [], clientId = '', accountLabel = 'A') => {
    const cleanTargetId = String(clientId).trim().toLowerCase();
    const trades = [];
    const ordersBySymbolDate = {};
    const seenTrades = new Set();

    records.forEach(r => {
        if (!r) return;
        
        // Strict client validation
        const itemClientId = String(r.client_id || r.clientId || r.client || '').trim().toLowerCase();
        if (itemClientId && cleanTargetId && itemClientId !== cleanTargetId) {
            return; // Reject trades that explicitly belong to a different client
        }

        // Check if item is already a complete or partial trade record (e.g. from client-audit)
        const hasLiveTradeFields = (r.buy_price_l !== undefined || r.sell_price_l !== undefined || r.buy_time_l || r.sell_time_l);
        const hasTradePrice = (r.buy_price !== undefined || r.entry_price !== undefined || r.buyPrice !== undefined) ||
                              (r.sell_price !== undefined || r.exit_price !== undefined || r.sellPrice !== undefined);

        if (hasLiveTradeFields || (hasTradePrice && r.side === undefined)) {
            const trade = normalizeAccountTrade(r, accountLabel);
            if (trade && trade.symbol) {
                const dedupKey = `${trade.symbol}_${trade.buyTime || ''}_${trade.sellTime || ''}_${trade.buyPrice || ''}_${trade.sellPrice || ''}`;
                if (!seenTrades.has(dedupKey)) {
                    seenTrades.add(dedupKey);
                    trades.push(trade);
                }
            }
            return;
        }

        // If it's a raw single order with side (Buy or Sell)
        const sym = cleanSymbol(r.symbol || r.tradingsymbol || r.symbol_l || '');
        if (!sym) return;

        const timeStr = r.order_time || r.traded_time || r.trade_time || r.created_at || r.timestamp || r.buy_time || r.sell_time || '';
        const dateKey = timeStr ? String(timeStr).split('T')[0].split(' ')[0] : 'today';
        const groupKey = `${sym}_${dateKey}`;

        if (!ordersBySymbolDate[groupKey]) {
            ordersBySymbolDate[groupKey] = {
                symbol: sym,
                strategy: cleanStrategy(r.strategy || r.strategy_tag || r.tag || 'Optimus'),
                buys: [],
                sells: [],
                clientId
            };
        }

        const sideVal = String(r.side || '').trim().toUpperCase();
        const isBuy = sideVal === '1' || sideVal === 'BUY' || sideVal === 'B';
        const isSell = sideVal === '-1' || sideVal === '2' || sideVal === 'SELL' || sideVal === 'S';
        const price = Number(r.traded_price ?? r.order_price ?? r.price ?? r.buy_price ?? r.sell_price ?? 0);
        const qty = Number(r.qty ?? r.quantity ?? r.traded_qty ?? 0);

        if (isBuy) {
            ordersBySymbolDate[groupKey].buys.push({ time: timeStr, price, qty, raw: r });
        } else if (isSell) {
            ordersBySymbolDate[groupKey].sells.push({ time: timeStr, price, qty, raw: r });
        } else {
            if (r.buy_price || r.buy_time) ordersBySymbolDate[groupKey].buys.push({ time: timeStr, price: Number(r.buy_price || price), qty, raw: r });
            else if (r.sell_price || r.sell_time) ordersBySymbolDate[groupKey].sells.push({ time: timeStr, price: Number(r.sell_price || price), qty, raw: r });
            else ordersBySymbolDate[groupKey].buys.push({ time: timeStr, price, qty, raw: r });
        }
    });

    // Pair raw orders by symbol & date into trades
    Object.values(ordersBySymbolDate).forEach(group => {
        const { symbol, strategy, buys, sells } = group;
        const maxLegs = Math.max(buys.length, sells.length);
        if (maxLegs === 0) return;

        for (let i = 0; i < maxLegs; i++) {
            const b = buys[i] || null;
            const s = sells[i] || null;
            const bPrice = b?.price !== null && b?.price !== undefined && b.price > 0 ? b.price : null;
            const sPrice = s?.price !== null && s?.price !== undefined && s.price > 0 ? s.price : null;
            const bTime = b?.time || null;
            const sTime = s?.time || null;

            // Only push if there is an actual executed leg
            if ((bPrice || sPrice) && (bTime || sTime)) {
                const dedupKey = `${symbol}_${bTime || ''}_${sTime || ''}_${bPrice || ''}_${sPrice || ''}`;
                if (!seenTrades.has(dedupKey)) {
                    seenTrades.add(dedupKey);
                    trades.push({
                        id: `${symbol}_${bTime || 'notime'}_${sTime || 'notime'}_${i}_${Math.random().toString(36).substring(2, 6)}`,
                        clientId,
                        symbol,
                        rawSymbol: symbol,
                        strategy: strategy || 'Optimus',
                        buyTime: bTime,
                        buyPrice: bPrice,
                        sellTime: sTime,
                        sellPrice: sPrice,
                        qty: b?.qty || s?.qty || null,
                        accountLabel,
                        rawTrade: { buy: b?.raw, sell: s?.raw }
                    });
                }
            }
        }
    });

    return trades;
};

/**
 * Match trades between Account A and Account B
 * @param {Array} tradesListA - Raw trades from Account A
 * @param {Array} tradesListB - Raw trades from Account B
 * @param {Object} accountAInfo - { client_id, name }
 * @param {Object} accountBInfo - { client_id, name }
 * @param {Object} options - Matching configuration options
 */
export const matchAccountTrades = (
    tradesListA = [],
    tradesListB = [],
    accountAInfo = { client_id: 'Account A' },
    accountBInfo = { client_id: 'Account B' },
    options = {}
) => {
    const {
        maxTimeDiffMinutes = 60, // Maximum execution delay window to consider the same trade leg
        slippageTolerancePct = 0.5 // Tolerance threshold in % for price difference
    } = options;

    const normTradesA = (tradesListA || []).map(t => normalizeAccountTrade(t, 'A')).filter(Boolean);
    const normTradesB = (tradesListB || []).map(t => normalizeAccountTrade(t, 'B')).filter(Boolean);

    const matchedPairs = [];
    const usedBIndices = new Set();

    // Iterate through Account A trades to find matching Account B trade
    normTradesA.forEach((tradeA, indexA) => {
        const timeA = parseTimeToMs(tradeA.buyTime || tradeA.sellTime);

        let bestMatchIndex = -1;
        let lowestTimeDiff = Infinity;

        normTradesB.forEach((tradeB, indexB) => {
            if (usedBIndices.has(indexB)) return;

            // Check Symbol match
            if (tradeA.symbol && tradeB.symbol && tradeA.symbol !== tradeB.symbol) {
                return;
            }

            // Check Strategy compatibility (if both exist)
            if (tradeA.strategy && tradeB.strategy && tradeA.strategy !== 'DEFAULT' && tradeB.strategy !== 'DEFAULT') {
                if (tradeA.strategy !== tradeB.strategy) {
                    return;
                }
            }

            // Check Time Proximity
            const timeB = parseTimeToMs(tradeB.buyTime || tradeB.sellTime);
            if (timeA && timeB) {
                const diffMs = Math.abs(timeA - timeB);
                const diffMins = diffMs / (1000 * 60);

                if (diffMins <= maxTimeDiffMinutes && diffMs < lowestTimeDiff) {
                    lowestTimeDiff = diffMs;
                    bestMatchIndex = indexB;
                }
            } else if (tradeA.symbol === tradeB.symbol) {
                // If timestamp is not parseable but symbol matches uniquely
                bestMatchIndex = indexB;
            }
        });

        if (bestMatchIndex !== -1) {
            usedBIndices.add(bestMatchIndex);
            const tradeB = normTradesB[bestMatchIndex];
            matchedPairs.push(createComparisonRow(tradeA, tradeB, accountAInfo, accountBInfo, slippageTolerancePct));
        } else {
            // Unmatched trade in Account A
            matchedPairs.push(createComparisonRow(tradeA, null, accountAInfo, accountBInfo, slippageTolerancePct));
        }
    });

    // Any remaining trades in Account B that were not paired with Account A
    normTradesB.forEach((tradeB, indexB) => {
        if (!usedBIndices.has(indexB)) {
            matchedPairs.push(createComparisonRow(null, tradeB, accountAInfo, accountBInfo, slippageTolerancePct));
        }
    });

    // Sort by buy/sell timestamp descending
    matchedPairs.sort((a, b) => {
        const tA = parseTimeToMs(a.buy_time_a || a.sell_time_a || a.buy_time_b || a.sell_time_b) || 0;
        const tB = parseTimeToMs(b.buy_time_a || b.sell_time_a || b.buy_time_b || b.sell_time_b) || 0;
        return tB - tA;
    });

    return matchedPairs;
};

/**
 * Formats paired trade row with validation details, slippages, and compatibility with chart modals
 */
function createComparisonRow(tradeA, tradeB, accA, accB, slippageTolerancePct) {
    const symbolA = tradeA?.symbol || '';
    const symbolB = tradeB?.symbol || '';
    const symbol = symbolA || symbolB;

    const stratA = tradeA?.strategy || '';
    const stratB = tradeB?.strategy || '';
    const strategy = stratA || stratB;

    const buyPriceA = tradeA?.buyPrice ?? null;
    const buyPriceB = tradeB?.buyPrice ?? null;
    const sellPriceA = tradeA?.sellPrice ?? null;
    const sellPriceB = tradeB?.sellPrice ?? null;

    const buyTimeA = tradeA?.buyTime || null;
    const buyTimeB = tradeB?.buyTime || null;
    const sellTimeA = tradeA?.sellTime || null;
    const sellTimeB = tradeB?.sellTime || null;

    const qtyA = tradeA?.qty ?? null;
    const qtyB = tradeB?.qty ?? null;

    // Calculate slippage & differences
    let slippageBuyPct = null;
    let slippageSellPct = null;
    let buyPriceDiff = null;
    let sellPriceDiff = null;
    let timeDiffSec = null;
    let comments = '';
    let status = 'Match';

    if (tradeA && tradeB) {
        // Both exist -> compare prices and execution time
        if (buyPriceA !== null && buyPriceB !== null) {
            buyPriceDiff = buyPriceB - buyPriceA;
            slippageBuyPct = buyPriceA > 0 ? (buyPriceDiff / buyPriceA) * 100 : 0;
        }

        if (sellPriceA !== null && sellPriceB !== null) {
            sellPriceDiff = sellPriceB - sellPriceA;
            slippageSellPct = sellPriceA > 0 ? (sellPriceDiff / sellPriceA) * 100 : 0;
        }

        const timeMsA = parseTimeToMs(buyTimeA || sellTimeA);
        const timeMsB = parseTimeToMs(buyTimeB || sellTimeB);
        if (timeMsA && timeMsB) {
            timeDiffSec = (timeMsB - timeMsA) / 1000;
        }

        const commentsList = [];
        let isMismatch = false;

        if (slippageBuyPct !== null && Math.abs(slippageBuyPct) > slippageTolerancePct) {
            isMismatch = true;
            commentsList.push(`Buy Price Diff: ₹${buyPriceDiff.toFixed(2)} (${slippageBuyPct > 0 ? '+' : ''}${slippageBuyPct.toFixed(2)}%)`);
        }

        if (slippageSellPct !== null && Math.abs(slippageSellPct) > slippageTolerancePct) {
            isMismatch = true;
            commentsList.push(`Sell Price Diff: ₹${sellPriceDiff.toFixed(2)} (${slippageSellPct > 0 ? '+' : ''}${slippageSellPct.toFixed(2)}%)`);
        }

        if (timeDiffSec !== null && Math.abs(timeDiffSec) > 3) {
            commentsList.push(`Delay: ${timeDiffSec > 0 ? '+' : ''}${timeDiffSec.toFixed(1)}s`);
        }

        if (qtyA !== null && qtyB !== null && qtyA !== qtyB) {
            commentsList.push(`Qty: ${qtyA} vs ${qtyB}`);
        }

        if (isMismatch) {
            status = 'Mismatch';
            comments = commentsList.join(' | ') || 'Price / Execution Slippage Mismatch';
        } else {
            status = 'Match';
            comments = commentsList.length > 0 ? commentsList.join(' | ') : 'Exact Execution Match';
        }
    } else if (tradeA && !tradeB) {
        status = 'Account A Only';
        comments = `Executed in ${accA?.client_id || 'Account A'}, missing in ${accB?.client_id || 'Account B'}`;
    } else if (!tradeA && tradeB) {
        status = 'Account B Only';
        comments = `Executed in ${accB?.client_id || 'Account B'}, missing in ${accA?.client_id || 'Account A'}`;
    }

    return {
        id: `${tradeA?.id || 'none'}_${tradeB?.id || 'none'}`,
        // Account A Fields
        account_a: accA?.client_id || 'Account A',
        symbol_a: symbolA,
        strategy_tag_a: stratA,
        buy_time_a: buyTimeA,
        buy_price_a: buyPriceA,
        sell_time_a: sellTimeA,
        sell_price_a: sellPriceA,
        qty_a: qtyA,
        raw_trade_a: tradeA?.rawTrade || null,

        // Account B Fields
        account_b: accB?.client_id || 'Account B',
        symbol_b: symbolB,
        strategy_tag_b: stratB,
        buy_time_b: buyTimeB,
        buy_price_b: buyPriceB,
        sell_time_b: sellTimeB,
        sell_price_b: sellPriceB,
        qty_b: qtyB,
        raw_trade_b: tradeB?.rawTrade || null,

        // Dual Aliases for Compatibility with TradeSignalValidationModal
        symbol_l: symbolA || symbolB,
        strategy_tag_l: stratA || stratB,
        buy_time_l: buyTimeA,
        buy_price_l: buyPriceA,
        sell_time_l: sellTimeA,
        sell_price_l: sellPriceA,
        qty_l: qtyA,

        // Validation & Comparison Results
        status,
        comments,
        slippage_buy: slippageBuyPct !== null ? slippageBuyPct : null,
        slippage_sell: slippageSellPct !== null ? slippageSellPct : null,
        buy_price_diff: buyPriceDiff,
        sell_price_diff: sellPriceDiff,
        time_diff_sec: timeDiffSec,
        symbol
    };
}
